import React, { FC, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Box from "@mui/material/Box";
import { Card, CardContent, CircularProgress, Typography } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Select from "@/components/Select";
import { useOrganizationUnitsByIds } from "@/hooks/useOrganizations";
import GenericAlertModal from "@/components/AlertModal";
import { useAppTheme } from "@/utils/theme";
import { useGetPrograms } from "@/hooks/usePrograms";
import { ProgramOrganization } from "@/domain/user.interface";

interface TabAssignOUProps {
  programsOptions: Array<{ key: string; value: string }>;
  hasAdminRole?: boolean;
  isSubmitting?: boolean;
  clearSubmitting?: () => void;
}

export const TabAssignOU: FC<TabAssignOUProps> = ({
  programsOptions,
  hasAdminRole,
  isSubmitting,
  clearSubmitting,
}) => {
  const theme = useAppTheme();
  const { control, watch, setValue } = useFormContext();

  const programId = watch("programId");
  const programs = watch("programs") || [];

  const { programs: programsData } = useGetPrograms({});

  const [showError, setShowError] = useState(false);

  // Validar cuando se hace submit
  useEffect(() => {
    if (!hasAdminRole && programs.length === 0 && isSubmitting) {
      setShowError(true);
      clearSubmitting?.();
    }
  }, [programs, isSubmitting, hasAdminRole, clearSubmitting]);

  // Obtener las OUs del programa seleccionado
  const currentProgramOrgUnitIds = React.useMemo(() => {
    if (!programId || !programsData) return [];
    const program = programsData.find((p) => p._id === programId);
    return program?.orgUnitIds || program?.orgUnitId || [];
  }, [programId, programsData]);

  const {
    organizationUnits,
    isLoadingOrganizationUnits,
    refetchOrganizationUnits,
  } = useOrganizationUnitsByIds({
    ids: currentProgramOrgUnitIds,
    status: "ACTIVE",
  });

  useEffect(() => {
    if (currentProgramOrgUnitIds.length > 0) {
      refetchOrganizationUnits();
    }
  }, [currentProgramOrgUnitIds, refetchOrganizationUnits]);

  // Función para actualizar programs cuando cambian las OUs
  const updatePrograms = (newOrgUnitIds: string[]) => {
    if (!programId) return;

    const updatedPrograms = [...programs];

    // Buscar si el programa actual ya existe en el array
    const existingProgramIndex = updatedPrograms.findIndex(
      (progOrg) => progOrg.programId === programId
    );

    if (newOrgUnitIds.length === 0) {
      // Si no hay OUs, remover el programa del array si existe
      if (existingProgramIndex !== -1) {
        updatedPrograms.splice(existingProgramIndex, 1);
      }
    } else {
      // Si hay OUs seleccionadas
      if (existingProgramIndex !== -1) {
        // El programa ya existe, actualizar sus OUs
        updatedPrograms[existingProgramIndex].orgUnitIds = newOrgUnitIds;
      } else {
        // El programa no existe, agregarlo con sus OUs
        updatedPrograms.push({
          programId: programId,
          orgUnitIds: newOrgUnitIds,
        });
      }
    }

    // Actualizar el formulario con la nueva estructura
    setValue("programs", updatedPrograms);
  };

  return (
    <>
      {hasAdminRole ? (
        <Typography>
          The users with account admin role have access to all programs and org
          units.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            width: "100%",
          }}
        >
          <Controller
            name="programId"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                handleChange={(value) => {
                  field.onChange(value);

                  // Cuando cambias de programa, buscar si ese programa ya tiene OUs seleccionadas
                  const existingProgram = programs.find(
                    (progOrg: ProgramOrganization) =>
                      progOrg.programId === value
                  );

                  // Si el programa ya existía, mostrar sus OUs marcadas
                  // Si es nuevo, no marcar nada (array vacío)
                  const existingOrgUnitIds = existingProgram?.orgUnitIds || [];

                  // Actualizar solo el campo orgUnitIds para mostrar las OUs del programa actual
                  setValue("orgUnitIds", existingOrgUnitIds);
                }}
                data={programsOptions || []}
                value={field.value}
                label="Programs"
                id="users-programs"
                isLoading={isLoadingOrganizationUnits}
                maxWidth={220}
              />
            )}
          />

          {isLoadingOrganizationUnits ? (
            <Card>
              <CardContent
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100px",
                }}
              >
                <CircularProgress />
              </CardContent>
            </Card>
          ) : organizationUnits && organizationUnits?.length > 0 ? (
            <Card>
              <CardContent>
                <Controller
                  name="orgUnitIds"
                  control={control}
                  defaultValue={[]}
                  render={({ field }) => (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        width: "100%",
                      }}
                    >
                      {organizationUnits?.map((unit) => (
                        <FormControlLabel
                          key={unit._id}
                          sx={{ mx: "0", px: "8px" }}
                          control={
                            <Checkbox
                              checked={field.value?.includes(unit._id)}
                              onChange={(e) => {
                                let newOrgUnitIds: string[];

                                if (e.target.checked) {
                                  newOrgUnitIds = [
                                    ...(field.value as string[]),
                                    unit._id,
                                  ];
                                } else {
                                  newOrgUnitIds = (
                                    field.value as string[]
                                  ).filter((id: string) => id !== unit._id);
                                }

                                // Actualizar el campo del formulario
                                field.onChange(newOrgUnitIds);

                                // Actualizar programs con la lógica inteligente
                                updatePrograms(newOrgUnitIds);
                              }}
                            />
                          }
                          label={unit.name}
                        />
                      ))}
                    </Box>
                  )}
                />
              </CardContent>
            </Card>
          ) : null}

          {showError && (
            <GenericAlertModal
              onCancel={() => setShowError(false)}
              onClick={() => setShowError(false)}
              title="Message"
              description="At least one program with organization units must be selected for this user."
              submitText="Ok"
              theme={theme}
            />
          )}
        </Box>
      )}
    </>
  );
};
